/**
 * Connect to Google, parent approval and who may buy (docs/plans/011-google-and-parent-approval.md): the Account sheet's state, and the
 * parents' page (`/parent?token=...`), which is a separate screen that never loads a game.
 */
import type { ConsentActions, ConsentView, ParentActions, ParentView } from "../ui/view-model";
import { AccountError, type AccountApi, type Approval } from "./api";

const codeOf = (error: unknown): string => (error instanceof AccountError ? error.code : "server_error");

export interface ConsentDeps {
  api: AccountApi;
  isSignedIn(): boolean;
  onChange(): void;
  /** Leaves the game for Google's sign-in (`window.location.assign`). */
  navigate(url: string): void;
}

export function createConsent(deps: ConsentDeps) {
  let loaded = false;
  let google = false;
  let approval: Approval = "not_needed";
  let canBuy = false;
  let missing: ("google" | "parent")[] = [];
  let approvalLink: { url: string; expiresAt: string } | null = null;
  let googleResult: "linked" | "error" | null = null;
  let problem: string | null = null;
  let busy = false;

  /** Runs one request: busy while it runs, the problem code kept for the screen, never an exception to the caller. */
  async function run(work: () => Promise<void>): Promise<string | null> {
    if (!deps.isSignedIn()) return "not_signed_in";
    busy = true;
    problem = null;
    deps.onChange();
    try {
      await work();
      return null;
    } catch (error) {
      problem = codeOf(error);
      return problem;
    } finally {
      busy = false;
      deps.onChange();
    }
  }

  const api = {
    view(): ConsentView {
      const signedIn = deps.isSignedIn();
      return {
        signedIn,
        loaded: signedIn && loaded,
        google: signedIn && google,
        approval: signedIn ? approval : "not_needed",
        canBuy: signedIn && canBuy,
        missing: signedIn ? [...missing] : [],
        approvalLink: signedIn ? approvalLink : null,
        googleResult,
        problem,
        busy,
      };
    },

    load: () =>
      run(async () => {
        const me = await deps.api.me();
        if (!me) throw new AccountError("not_signed_in");
        google = me.google;
        approval = me.approval;
        const gate = await deps.api.purchase();
        canBuy = gate.canBuy;
        missing = gate.missing;
        loaded = true;
      }),

    connectGoogle: () =>
      run(async () => {
        deps.navigate(await deps.api.startGoogle());
      }),

    makeApprovalLink: () =>
      run(async () => {
        approvalLink = await deps.api.requestApproval();
      }),

    /** Google's answer from the address (`?google=linked|error`). Anything else is ignored. */
    setGoogleResult(raw: string) {
      if (raw !== "linked" && raw !== "error") return;
      googleResult = raw;
      deps.onChange();
    },

    dismissGoogleResult() {
      googleResult = null;
      deps.onChange();
    },

    /** Forget everything: sign-out, account deletion. */
    clear() {
      loaded = false;
      google = false;
      approval = "not_needed";
      canBuy = false;
      missing = [];
      approvalLink = null;
      problem = null;
      busy = false;
      deps.onChange();
    },
  };
  return api;
}

export type Consent = ReturnType<typeof createConsent>;

export function consentActions(consent: Consent): ConsentActions {
  return {
    onLoadConsent: async () => void (await consent.load()),
    onConnectGoogle: async () => void (await consent.connectGoogle()),
    onMakeApprovalLink: async () => void (await consent.makeApprovalLink()),
    onDismissGoogleResult: () => consent.dismissGoogleResult(),
  };
}

export interface ParsedAddress {
  page: "game" | "parent";
  /** The parent's link token (parents' page only). */
  token: string | null;
  /** What happened at Google for a parent: shown instead of the question. */
  result: "approved" | "withdrawn" | "error" | null;
  /** Google's answer for the player (game page only). */
  google: "linked" | "error" | "signedin" | "pending" | null;
  /** The address to put in the bar instead, without `?google=`; null when there is nothing to remove. */
  cleaned: string | null;
}

export function parseAddress(search: string, pathname: string, hash: string): ParsedAddress {
  const params = new URLSearchParams(search);
  if (/^\/parent\/?$/.test(pathname)) {
    const result = params.get("result");
    return {
      page: "parent",
      token: params.get("token"),
      result: result === "approved" || result === "withdrawn" || result === "error" ? result : null,
      google: null,
      cleaned: null,
    };
  }
  if (!params.has("google")) return { page: "game", token: null, result: null, google: null, cleaned: null };
  const raw = params.get("google");
  params.delete("google");
  const rest = params.toString();
  return { page: "game", token: null, result: null, google: raw === "linked" || raw === "error" || raw === "signedin" || raw === "pending" ? raw : null, cleaned: `${pathname}${rest ? `?${rest}` : ""}${hash}` };
}

export interface ParentDeps {
  api: AccountApi;
  token: string | null;
  result: "approved" | "withdrawn" | "error" | null;
  onChange(): void;
  navigate(url: string): void;
}

export function createParentPage(deps: ParentDeps) {
  let status: ParentView["status"] = deps.result ?? (deps.token === null || deps.token.length < 20 ? "invalid" : "checking");
  let mode: "approve" | "withdraw" = "approve";

  const set = (s: ParentView["status"]) => {
    status = s;
    deps.onChange();
  };
  const failure = (error: unknown): ParentView["status"] => {
    const code = codeOf(error);
    return code === "offline" ? "offline" : code === "google_unavailable" ? "unavailable" : code === "invalid_link" ? "invalid" : "error";
  };

  return {
    view: (): ParentView => ({ status }),

    /** Checks the link (nothing to check when Google has just sent the parent back). */
    async start() {
      if (status !== "checking") return;
      try {
        const found = await deps.api.lookupApproval(deps.token!);
        if (!found.valid) return set("invalid");
        mode = found.kind;
        set(found.kind);
      } catch (error) {
        set(failure(error));
      }
    },

    async continueWithGoogle() {
      if (status !== "approve" && status !== "withdraw") return;
      set("starting");
      try {
        deps.navigate(await deps.api.startApprovalSignIn(deps.token!, mode));
      } catch (error) {
        set(failure(error));
      }
    },
  };
}

export type ParentPage = ReturnType<typeof createParentPage>;

export function parentActions(page: ParentPage): ParentActions {
  return { onContinueWithGoogle: () => page.continueWithGoogle() };
}
